import { useEffect, useRef } from "react";
import ConditionalTextbox from "@components/ConditionalTextbox/ConditionalTextbox";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import {
  FORM_FIELDS,
  type VesselNominationOutletContext,
} from "@features/vessel-nomination/add/constants";
import type { VesselNominationFormData } from "@features/vessel-nomination/add/schema/vesselNominationFormSchema";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { LANG_BASE } from "../PreArrivalQuestionnaire";

const Mooring = () => {
  const { t } = useTranslation();
  const { formData, updateFormData } = useOutletContext<VesselNominationOutletContext>();
  const { trigger, watch } = formData;

  const mooringFwd = watch(FORM_FIELDS.MOORING_FWD);
  const mooringMid = watch(FORM_FIELDS.MOORING_MID);
  const mooringAft = watch(FORM_FIELDS.MOORING_AFT);
  const previousValuesRef = useRef({
    mooringFwd,
    mooringMid,
    mooringAft,
  });

  const hasMooringDetailsAndDeficiencies = watch(FORM_FIELDS.HAS_MOORING_DETAILS_AND_DEFICIENCIES);
  const hasAnyDefectsHullMachineryEquipment = watch(
    FORM_FIELDS.HAS_ANY_DEFECTS_HULL_MACHINERY_EQUIPMENT,
  );
  const hasAreaWithProtrusion = watch(FORM_FIELDS.HAS_AREA_WITH_PROTRUSION);

  useEffect(() => {
    const previousValues = previousValuesRef.current;

    if (mooringFwd !== previousValues.mooringFwd) {
      if (mooringMid) void trigger(FORM_FIELDS.MOORING_MID);
      if (mooringAft) void trigger(FORM_FIELDS.MOORING_AFT);
    }

    if (mooringMid !== previousValues.mooringMid) {
      if (mooringFwd) void trigger(FORM_FIELDS.MOORING_FWD);
      if (mooringAft) void trigger(FORM_FIELDS.MOORING_AFT);
    }

    if (mooringAft !== previousValues.mooringAft) {
      if (mooringFwd) void trigger(FORM_FIELDS.MOORING_FWD);
      if (mooringMid) void trigger(FORM_FIELDS.MOORING_MID);
    }
  }, [mooringFwd, mooringMid, mooringAft, trigger]);

  return (
    <FormProvider {...formData}>
      <FormSection
        title={t(`${LANG_BASE}.sections.mooring`)}
        className="grid-cols-1 lg:grid-cols-2 xl:grid-cols-3"
      >
        <FormItem<VesselNominationFormData, typeof FORM_FIELDS.MOORING_FWD>
          testId="nomination-mooring-fwd-input"
          name={FORM_FIELDS.MOORING_FWD}
          type="number"
          label={t(`${LANG_BASE}.fields.fwd`)}
          onFieldChange={updateFormData}
          maxDigits={8}
          decimals={3}
          adornment="m"
          thousandSeparator
          canBeginWithZero={false}
        />
        <FormItem<VesselNominationFormData, typeof FORM_FIELDS.MOORING_MID>
          testId="nomination-mooring-mid-input"
          name={FORM_FIELDS.MOORING_MID}
          type="number"
          label={t(`${LANG_BASE}.fields.mid`)}
          onFieldChange={updateFormData}
          maxDigits={8}
          decimals={3}
          adornment="m"
          thousandSeparator
          canBeginWithZero={false}
        />
        <FormItem<VesselNominationFormData, typeof FORM_FIELDS.MOORING_AFT>
          testId="nomination-mooring-aft-input"
          name={FORM_FIELDS.MOORING_AFT}
          type="number"
          label={t(`${LANG_BASE}.fields.aft`)}
          onFieldChange={updateFormData}
          maxDigits={8}
          decimals={3}
          adornment="m"
          thousandSeparator
          canBeginWithZero={false}
        />
        <ConditionalTextbox<
          VesselNominationFormData,
          typeof FORM_FIELDS.HAS_MOORING_DETAILS_AND_DEFICIENCIES,
          typeof FORM_FIELDS.MOORING_DETAILS_AND_DEFICIENCIES
        >
          label={t(`${LANG_BASE}.fields.mooring-details-and-deficiencies`)}
          textboxFieldName={FORM_FIELDS.MOORING_DETAILS_AND_DEFICIENCIES}
          radioFieldName={FORM_FIELDS.HAS_MOORING_DETAILS_AND_DEFICIENCIES}
          onFieldChange={updateFormData}
          textboxDisabled={!hasMooringDetailsAndDeficiencies}
          textboxMaxChars={500}
        />
        <ConditionalTextbox<
          VesselNominationFormData,
          typeof FORM_FIELDS.HAS_ANY_DEFECTS_HULL_MACHINERY_EQUIPMENT,
          typeof FORM_FIELDS.ANY_DEFECTS_HULL_MACHINERY_EQUIPMENT
        >
          label={t(`${LANG_BASE}.fields.any-defects-hull-machinery-equipment`)}
          textboxFieldName={FORM_FIELDS.ANY_DEFECTS_HULL_MACHINERY_EQUIPMENT}
          radioFieldName={FORM_FIELDS.HAS_ANY_DEFECTS_HULL_MACHINERY_EQUIPMENT}
          onFieldChange={updateFormData}
          textboxDisabled={!hasAnyDefectsHullMachineryEquipment}
          textboxMaxChars={500}
        />
        <ConditionalTextbox<
          VesselNominationFormData,
          typeof FORM_FIELDS.HAS_AREA_WITH_PROTRUSION,
          typeof FORM_FIELDS.AREA_WITH_PROTRUSION
        >
          label={t(`${LANG_BASE}.fields.area-with-protrusion`)}
          textboxFieldName={FORM_FIELDS.AREA_WITH_PROTRUSION}
          radioFieldName={FORM_FIELDS.HAS_AREA_WITH_PROTRUSION}
          onFieldChange={updateFormData}
          textboxDisabled={!hasAreaWithProtrusion}
          textboxMaxChars={100}
        />
      </FormSection>
    </FormProvider>
  );
};

export default Mooring;
